import * as React from 'react';
import { cn } from '../lib/cn';
import type { LegalBlock, LegalDoc } from '../legal/types';

/**
 * Renders a legal document (Terms, Privacy Policy, ...) with a numbered
 * table of contents: a sticky sidebar on desktop, a collapsible list on
 * mobile. Section headings carry anchor ids so clauses can be deep-linked
 * (e.g. /terms#refunds).
 */
export interface LegalDocumentProps extends React.HTMLAttributes<HTMLElement> {
  doc: LegalDoc;
}

export function LegalDocument({ doc, className, ...props }: LegalDocumentProps) {
  return (
    <article className={cn('text-foreground', className)} {...props}>
      <header className="border-border border-b pb-8">
        <p className="text-accent text-xs font-semibold uppercase tracking-widest">Legal</p>
        <h1 className="font-display mt-3 text-4xl font-semibold leading-tight sm:text-5xl">
          {doc.title}
        </h1>
        <p className="text-muted-foreground mt-3 max-w-2xl text-base">{doc.summary}</p>
        <p className="text-muted-foreground mt-4 text-xs">Last updated: {doc.lastUpdated}</p>
      </header>

      <div className="mt-8 grid gap-10 lg:grid-cols-4">
        <nav
          aria-label="Contents"
          className="lg:sticky lg:top-24 lg:col-span-1 lg:max-h-screen lg:self-start lg:overflow-y-auto lg:pb-24"
        >
          <details className="border-border group rounded-md border lg:hidden">
            <summary className="cursor-pointer select-none px-4 py-3 text-sm font-semibold">
              Contents
            </summary>
            <TableOfContents doc={doc} className="border-border border-t px-4 py-3" />
          </details>
          <div className="hidden lg:block">
            <p className="text-muted-foreground text-xs font-semibold uppercase tracking-widest">
              Contents
            </p>
            <TableOfContents doc={doc} className="mt-3" />
          </div>
        </nav>

        <div className="min-w-0 max-w-3xl lg:col-span-3">
          <div className="space-y-4">
            {doc.intro.map((block, i) => (
              <Block key={i} block={block} />
            ))}
          </div>

          {doc.sections.map((section, i) => (
            <section key={section.id} id={section.id} className="mt-10 scroll-mt-24">
              <h2 className="font-display text-2xl font-semibold">
                <span className="text-accent mr-2">{i + 1}.</span>
                {section.heading}
              </h2>
              <div className="mt-4 space-y-4">
                {section.blocks.map((block, j) => (
                  <Block key={j} block={block} />
                ))}
              </div>
            </section>
          ))}
        </div>
      </div>
    </article>
  );
}

function TableOfContents({ doc, className }: { doc: LegalDoc; className?: string }) {
  return (
    <ol className={cn('space-y-2 text-sm', className)}>
      {doc.sections.map((section, i) => (
        <li key={section.id}>
          <a
            href={`#${section.id}`}
            className="text-muted-foreground hover:text-foreground flex gap-2 transition-colors"
          >
            <span className="w-5 shrink-0 tabular-nums">{i + 1}.</span>
            <span>{section.heading}</span>
          </a>
        </li>
      ))}
    </ol>
  );
}

const PARAGRAPH = 'text-sm leading-relaxed text-foreground/90 sm:text-base';

function Block({ block }: { block: LegalBlock }) {
  if (typeof block === 'string') return <p className={PARAGRAPH}>{block}</p>;

  if ('subheading' in block) {
    return <h3 className="pt-2 text-base font-semibold">{block.subheading}</h3>;
  }

  if ('note' in block) {
    return (
      <p className="border-accent/40 bg-accent/10 rounded-md border px-4 py-3 text-sm font-medium leading-relaxed">
        {block.note}
      </p>
    );
  }

  if ('link' in block) {
    return (
      <p className={PARAGRAPH}>
        {block.lead ? `${block.lead} ` : null}
        <a
          href={block.href}
          className="text-foreground decoration-accent hover:text-accent break-words font-semibold underline underline-offset-4 transition-colors"
        >
          {block.link}
        </a>
      </p>
    );
  }

  const List = block.ordered ? 'ol' : 'ul';
  return (
    <List
      className={cn(
        PARAGRAPH,
        'marker:text-muted-foreground space-y-2 pl-6',
        block.ordered ? 'list-decimal' : 'list-disc',
      )}
    >
      {block.list.map((item, i) => (
        <li key={i} className="pl-1">
          {item}
        </li>
      ))}
    </List>
  );
}
